import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CustomLink from "./CustomLink";
import DropdownMenu from "./DropdownMenu";
import MobileMenu from "./MobileMenu"; // Importa el menú móvil
import { fetchMenus, fetchGlobalConfig } from "../utils/contentfulRequests"; // Importa las funciones centralizadas
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";

function Header() {
  const [menuData, setMenuData] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Estado del menú móvil
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:1024px)'); // Menú hamburguesa en <=1024px

  const [isScrolling, setIsScrolling] = useState(false); // Estado para el Header flotante

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolling(true); // Muestra el Header al desplazarse
      } else {
        setIsScrolling(false); // Oculta el Header si está en la parte superior
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [menus, config] = await Promise.all([fetchMenus(), fetchGlobalConfig()]);
      setMenuData(menus);
      setGlobalConfig(config);
    };

    fetchData();
  }, []);

  const openMenu = (index) => {
    setTimeout(() => {
      setMenuOpen(index);
    }, 200);
  };

  const closeMenu = () => {
    setTimeout(() => setMenuOpen(null), 200);
  };

  return (
    <>
      {/* Barra superior con el número de teléfono */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          height: "34px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: 2,
        }}
      >
        {globalConfig && (
          <Typography
            className="phone"
            variant="body2"
            component={isMobile ? "a" : "p"} // Cambia a un enlace solo en móviles
            href={isMobile ? `tel:${globalConfig.telefono}` : undefined} // Agrega el atributo "tel:" solo si es móvil
            sx={{
              color: theme.palette.white.main,
              fontWeight: "bold",
              textDecoration: "none", // Elimina subrayado del enlace
              cursor: isMobile ? "pointer" : "default", // Cambia el cursor a pointer solo en móviles
            }}
          >
            Llámanos: {globalConfig.telefono}
          </Typography>
        )}
      </Box>

      {/* Barra principal */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: isScrolling ? "#EBEEF3" : "transparent",
          boxShadow: isScrolling ? theme.shadows[3] : 'none',
          borderBottom: '1px solid #e0e3e8',
          transition: "background-color 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1), border-bottom 0.25s cubic-bezier(.4,2,.6,1)",
          zIndex: theme.zIndex.appBar + 1,
          // position: 'relative',
            '::after': !isScrolling ? {
              content: '""',
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%',
              height: '1.5px',
              background: 'linear-gradient(90deg, #bdbdbd 0%, #F86B16 40%, #e0e0e0 60%, #bdbdbd 100%)',
              backgroundSize: '200% 100%',
              animation: 'ledBarAnim 3s linear infinite',
              opacity: 0.85,
              pointerEvents: 'none',
            } : {},
          '@keyframes ledBarAnim': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '0% 0' },
          },
        }}
      >
        <Toolbar
          sx={{
            minWidth: 0,
            overflowX: isMobile ? "hidden" : "auto",
            flexWrap: "nowrap",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: theme.palette.highlight.main, borderRadius: 4 },
          }}
        >
          {/* Logo del Sitio */}
          {globalConfig && (
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                flex: "1 1 auto",
                minWidth: 120,
                flexShrink: 0,
              }}
            >
              <img
                src={globalConfig.logo}
                alt="Logo"
                style={{
                  height: "40px",
                  marginRight: "10px",
                }}
              />
            </Box>
          )}

          {/* Menú móvil */}
          {isMobile ? (
            <MobileMenu
              menuData={menuData}
              drawerOpen={mobileMenuOpen}
              toggleDrawer={setMobileMenuOpen}
              logo={globalConfig ? globalConfig.logo : null}
            />
          ) : (
            // Menús para escritorio
            <Box sx={{ display: "flex", flexWrap: "nowrap", flexShrink: 0 }}>
              {menuData.map((menu, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => openMenu(index)}
                  sx={{ display: "inline-block", flexShrink: 0 }}
                >
                  {menu.type === "Lista" ? (
                    <>
                      <CustomLink hasDropdown isHovered={menuOpen === index}>
                        {menu.name}
                      </CustomLink>
                      <DropdownMenu
                        menuOpen={menuOpen === index}
                        items={menu.subMenus}
                        onMouseEnter={() => openMenu(index)}
                        onMouseLeave={closeMenu}
                        onCloseMenu={closeMenu}
                      />
                    </>
                  ) : (
                    <CustomLink to={menu.link}>{menu.name}</CustomLink>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}

export default Header;
